import React, { useRef, useEffect, memo, useMemo, useCallback, Fragment, useState } from 'react'
import PropTypes from 'prop-types'
import { Map, OrderedMap, List } from 'immutable'

import shelfStyle from '@aytm/app/swidget/questions/styles/sh/shelf.module.scss'
import { putToArray } from '@aytm/lib/common/array'

import { SETTINGS } from '@aytm/app/swidget/questions/components/sh/settings'
import { TopShelf } from '@aytm/app/swidget/questions/components/sh/topShelf'
import ShelvesContainer from '@aytm/lib/shelves/container'

import bottomImage from '@aytm/app/swidget/questions/images/sh/bottom.png'
import Prices from '@aytm/app/swidget/questions/components/sh/prices'

Shelves.propTypes = {
  questionSchema: PropTypes.instanceOf(Map).isRequired,
  fullscreen: PropTypes.bool,
  scrolling: PropTypes.bool,
  mobile: PropTypes.bool,
  first: PropTypes.bool,
  // pallet multi-section: 0-based section index, 0..palletSectionsCount-1.
  palletSectionIndex: PropTypes.number,
  palletSectionsCount: PropTypes.number,
  last: PropTypes.bool,
  generateImage: PropTypes.func,
  emulatedMobile: PropTypes.bool,
  externalHover: PropTypes.bool,
  shelvesNumbers: PropTypes.array,
  widthFactor: PropTypes.number.isRequired,
  cartCount: PropTypes.number,
  blockWidths: PropTypes.array,
  config: PropTypes.instanceOf(List).isRequired,
  getContainer: PropTypes.func,
  width: PropTypes.number.isRequired,
  answers: PropTypes.instanceOf(OrderedMap).isRequired,
  takenGoods: PropTypes.instanceOf(List).isRequired,
  onProductClick: PropTypes.func,
  onHovered: PropTypes.func,
  onRender: PropTypes.func,
  answersImages: PropTypes.any
}

export function Shelves(props) {
  const {
    questionSchema,
    fullscreen,
    scrolling,
    mobile,
    widthFactor,
    width,
    answers,
    takenGoods,
    onProductClick,
    onHovered,
    first,
    palletSectionIndex = 0,
    palletSectionsCount = 1,
    last,
    externalHover,
    cartCount,
    getContainer,
    generateImage,
    answersImages,
    onRender,
    config,
    shelvesNumbers,
    blockWidths,
    emulatedMobile
  } = props

  // pallet with palletSectionsCount > 1: 0-based section split. Non-pallet keeps legacy first/double_width.
  const isPalletMulti = 'pallet' == questionSchema.get('shelf_type') && palletSectionsCount > 1
  // unified column count: pallet uses palletSectionsCount; non-pallet uses (double_width ? 2 : 1).
  // Replaces former `double_width ? width/2 : width` divisions so migrated 2-section pallets keep their layout.
  const sectionsCount = isPalletMulti ? palletSectionsCount : questionSchema.get('double_width') ? 2 : 1
  const widthIsSplit = sectionsCount > 1
  const inSection = i => {
    if (isPalletMulti) {
      const factor = SETTINGS[questionSchema.get('store')][questionSchema.get('shelf_type')].double ? 2 : 1
      const cycle = palletSectionsCount * factor
      const within = i % cycle
      return within >= palletSectionIndex * factor && within < (palletSectionIndex + 1) * factor
    }
    if (questionSchema.get('double_width')) {
      const dbl = SETTINGS[questionSchema.get('store')][questionSchema.get('shelf_type')].double
      return dbl ? (first && i % 4 < 2) || (!first && i % 4 > 1) : (first && 0 == i % 2) || (!first && 1 == i % 2)
    }
    return true
  }
  const isFirstSection = isPalletMulti ? palletSectionIndex === 0 : first

  const settings = {
    ...SETTINGS[questionSchema.get('store')][questionSchema.get('shelf_type')],
    ...questionSchema.get('shelf_config').toJS(),
    ...questionSchema.getIn(['shelf_config', 'front']).toJS(),
    customConfig: config,
    widthFactor: widthFactor,
    width: width,
    first: isFirstSection,
    palletSectionIndex: palletSectionIndex,
    palletSectionsCount: palletSectionsCount,
    scrolling: scrolling,
    blockWidths: blockWidths,
    doubleWidth: questionSchema.get('double_width'),
    align: questionSchema.get('shelf_align'),
    id: questionSchema.get('id'),
    emulatedMobile: emulatedMobile,
    getContainer: getContainer,
    last: last,
    beginner: 0 == shelvesNumbers[0],
    firstShelf: questionSchema.get('double_width') && !first ? shelvesNumbers[1] : shelvesNumbers[0],
    noPerspective: 'no' == questionSchema.get('stock_type'),
    bottomAdditionalHeight:
      questionSchema.get('allow_custom_config') && 'pallet' == questionSchema.get('shelf_type')
        ? 0
        : SETTINGS[questionSchema.get('store')][questionSchema.get('shelf_type')].bottomAdditionalHeight
  }
  const shelvesSchema = useMemo(
    () =>
      questionSchema
        .get('answers_mask')
        .filter((_shelf, i) => inSection(i))
        .slice(shelvesNumbers[0], shelvesNumbers[1]),
    // disabled: questionSchema, settings are const
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [first, palletSectionIndex, palletSectionsCount]
  )

  const coordinates = useMemo(
    () =>
      questionSchema
        .getIn(['coordinates', 'coordinates'])
        .filter((_shelf, i) => inSection(i))
        .slice(shelvesNumbers[0], shelvesNumbers[1]),
    // disabled: questionSchema, settings are const
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [first, palletSectionIndex, palletSectionsCount]
  )

  const counts = useMemo(
    () =>
      questionSchema.getIn(['coordinates', 'counts'])
        ? questionSchema
            .getIn(['coordinates', 'counts'])
            .filter((_shelf, i) => inSection(i))
            .slice(shelvesNumbers[0], shelvesNumbers[1])
        : questionSchema
            .getIn(['coordinates', 'coordinates'])
            .filter((_shelf, i) => inSection(i))
            .slice(shelvesNumbers[0], shelvesNumbers[1])
            .map(c => c.map(_b => 1)),
    // disabled: questionSchema, settings are const
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [first, palletSectionIndex, palletSectionsCount]
  )

  const shelvesMask = settings.double
    ? shelvesSchema.reduce(
        (prev, __cur, i) =>
          !(i % 2)
            ? prev.push(
                List(
                  shelvesSchema.get(i).concat(shelvesSchema.get(i + 1) || List([]))
                  // .concat(shelvesSchema.get(i + 2) || List([]))
                  // .concat(shelvesSchema.get(i + 3) || List([]))
                )
              )
            : prev,
        List([])
      )
    : shelvesSchema

  // const leftArray = useRef([])

  const takenGoodsRef = useRef()
  takenGoodsRef.current = takenGoods

  const answersMask = useMemo(() => {
    const mask = []
    shelvesSchema.forEach((shelf, k) => {
      let width = 0
      const perspective =
        settings.perspective != undefined && k == shelvesSchema.size - 1 ? parseFloat(settings.perspective) : 1
      shelf.forEach((product, j) => {
        ;[...Array(product.get(1))].forEach((_item, i) => {
          width = width + answers.get(product.get(0)).get('width') * (perspective != 1 ? perspective : 1)
          putToArray(mask, [k, j, i], width)
        })
      })
      putToArray(mask, [k, -1], width)
    })
    return mask
    // disabled: settings is const
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shelvesSchema, answers])

  const container = useRef()
  const [hovered, setHovered] = useState(null)

  const onHover = useCallback(
    img => {
      setHovered(img)
      if ('function' == typeof onHovered) onHovered(img)
    },
    [onHovered]
  )

  const renderProducts = useCallback(
    takenGoods => {
      container.current.drawScene(
        settings,
        answers,
        answersMask,
        coordinates,
        counts,
        shelvesSchema,
        takenGoods,
        answersImages,
        onHover
      )
      if ('function' === typeof onRender) onRender(true)
    },
    // disabled: settings, questionSchema, shelvesSchema are consts
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const pixiDiv = useRef()
  // const currentGoodsState = useRef()
  const topShelf = 'pallet' == questionSchema.get('shelf_type') && settings.double && shelvesSchema.size < 3

  //for running survey
  const backupPack =
    'displayFridge' == questionSchema.get('shelf_type') && 'planogram' == questionSchema.get('flow_type')

  const containerHeight = settings.customConfig
    ? (topShelf ? settings.topFirst : settings.top) +
      settings.customConfig.reduce(
        (prev, cur, i) =>
          i < Math.trunc(shelvesSchema.size / (settings.double ? 2 : 1))
            ? prev +
              // settings.bottom +
              widthFactor *
                (cur.get('height') +
                  ('pegboard' == cur.get('shelf_type')
                    ? settings.pegAir
                    : questionSchema.get('version') > 0 && 'regular' == questionSchema.get('shelf_type')
                      ? 0
                      : settings.air) *
                    (backupPack ? 0 : 1))
            : prev,
        0
      ) +
      (settings.double ? settings.bottomAdditionalHeight * widthFactor : 0)
    : Math.trunc(shelvesSchema.size / (settings.double ? 2 : 1)) *
        ((settings.height + (questionSchema.get('version') > 0 ? 0 : settings.air)) * widthFactor + settings.bottom) +
      (topShelf ? settings.topFirst : settings.top) +
      (settings.double ? settings.bottomAdditionalHeight * widthFactor : 0)

  if (window['AYTM_shelfImage_' + questionSchema.get('id')])
    window['AYTM_shelfImage_' + questionSchema.get('id')].height = Math.max(
      window['AYTM_shelfImage_' + questionSchema.get('id')].height || 0,
      containerHeight
    )

  useEffect(() => {
    container.current = new ShelvesContainer(
      pixiDiv.current,
      width / (widthIsSplit && settings.double ? sectionsCount : 1),
      containerHeight,
      onProductClick,
      generateImage,
      topShelf
    )

    setTimeout(() => {
      // renderProducts(takenGoodsRef.current)
      //container.current.stopTicker(30000)
    }, 10)

    return () => {
      container.current.destroy()
    }
    // disabled: settings, questionSchema, shelvesSchema are consts
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, widthFactor])

  useEffect(() => {
    container.current.ticker.start()

    container.current.removeChildren()
    renderProducts(takenGoodsRef.current)
  }, [cartCount, renderProducts])

  return (
    // <Fragment>
    <Fragment>
      <div
        style={{ position: 'pallet' == questionSchema.get('shelf_type') ? 'relative' : 'static' }}
        id={questionSchema.get('id')}
      >
        {
          <style
            type="text/css"
            dangerouslySetInnerHTML={{
              __html:
                `
                #${questionSchema.get('id')} > * {
                .shelf-test-pixi-div canvas {
                  width: ${
                    widthIsSplit && !settings.fridge
                      ? width / sectionsCount + 'px'
                      : settings.fridge
                        ? width + 'px'
                        : '100%'
                  } !important;
                }
                }` +
                (settings.dots
                  ? `.swidget-shelves:before, .swidget-shelves:after {
            background-image: url(` +
                    require('./svg/dots.svg') +
                    `) !important;
          }
          `
                  : ``)
            }}
          />
        }
        <div
          className={
            `${shelfStyle.shelves} ${shelfStyle[settings.styleClass]} ` +
            (!first ? `${shelfStyle.second} ` : ``) +
            (fullscreen ? `${shelfStyle.fullscreen} ` : ``) +
            (topShelf ? `${shelfStyle.topShelved} ` : ``) +
            (mobile ? `${shelfStyle.mobile}` : ``)
          }
          data-testid="SW-SH-Shelves"
          style={{
            width: widthIsSplit && !settings.fridge ? width / sectionsCount + (settings.holders ? 10 : 0) : width
          }}
        >
          {!settings.fridge && !topShelf && (
            <div
              className={
                `${shelfStyle.firstShelf} ${shelfStyle.shelf} ${shelfStyle[settings.styleClass]} ` +
                (settings.customConfig &&
                settings.customConfig.getIn([0, 'shelf_type']) &&
                'pegboard' == settings.customConfig.getIn([0, 'shelf_type'])
                  ? `${shelfStyle['style' + settings.customConfig.getIn([0, 'shelf_type'])]} `
                  : ``)
              }
              data-testid="SW-SH-Shelf"
              data-sections={palletSectionsCount}
              style={{
                width:
                  (widthIsSplit && !settings.fridge
                    ? (width - settings.walls) / sectionsCount
                    : width - settings.walls) + 1
              }}
            >
              {settings.holders && (
                <div
                  className={shelfStyle.shelfBefore}
                  style={{
                    backgroundImage: settings.holders
                      ? 'url(' + require('./svg/' + settings.styleClass + 'Holders.svg') + ')'
                      : 'none'
                  }}
                />
              )}
              <div className={`${shelfStyle.bottomShelf} ${shelfStyle.topItem}`}>
                <div className={shelfStyle.before} />
                <div className={shelfStyle.after} />
              </div>
              {settings.holders && (
                <div
                  className={shelfStyle.shelfAfter}
                  style={{
                    backgroundImage: settings.holders
                      ? 'url(' + require('./svg/' + settings.styleClass + 'Holders.svg') + ')'
                      : 'none'
                  }}
                />
              )}
            </div>
          )}
          <div
            className={
              `${shelfStyle.productShelves}   ` +
              (questionSchema.get('double_width')
                ? first
                  ? `${shelfStyle.leftShelves} `
                  : `${shelfStyle.rightShelves} `
                : ``)
            }
            data-testid="SW-SH-ProductShelf"
          >
            {shelvesMask.map((shelf, i) => (
              <Fragment key={'shelf' + i}>
                {topShelf && (
                  <TopShelf
                    questionSchema={questionSchema}
                    width={widthIsSplit ? width / sectionsCount + (settings.holders ? 10 : 0) : width}
                    widthFactor={widthFactor}
                    mobile={mobile}
                    shelf={shelf}
                    answers={answers}
                    onProductClick={onProductClick}
                  />
                )}
                <div
                  className={
                    `${shelfStyle.shelf} ${
                      shelfStyle[
                        'shelf' + (settings.double && settings.topShelf ? (i == shelvesMask.size - 1 ? 1 : 0) : i)
                      ]
                    } ${shelfStyle[settings.styleClass]} ` +
                    (i == questionSchema.get('answers_mask').size / (questionSchema.get('double_width') ? 2 : 1) - 1
                      ? `${shelfStyle.last} `
                      : ``) +
                    (i == questionSchema.get('answers_mask').size / (questionSchema.get('double_width') ? 2 : 1) - 2
                      ? `${shelfStyle.penult} `
                      : ``) +
                    (settings.customConfig &&
                    settings.customConfig.getIn([i, 'shelf_type']) &&
                    'pegboard' == settings.customConfig.getIn([i, 'shelf_type'])
                      ? `${shelfStyle['style' + settings.customConfig.getIn([i, 'shelf_type'])]} `
                      : ``) +
                    (i &&
                    settings.customConfig &&
                    settings.customConfig.getIn([i - 1, 'shelf_type']) &&
                    'pegboard' == settings.customConfig.getIn([i - 1, 'shelf_type'])
                      ? `${shelfStyle['prevStyle' + settings.customConfig.getIn([i - 1, 'shelf_type'])]} `
                      : ``)
                  }
                  data-testid="SW-SH-Shelf"
                  style={{
                    minHeight:
                      ((settings.customConfig ? settings.customConfig.getIn([i, 'height']) : settings.height) +
                        (i == shelvesMask.size - 1 ? settings.bottomAdditionalHeight || 0 : 0) +
                        (settings.customConfig && 'pegboard' == settings.customConfig.getIn([i, 'shelf_type'])
                          ? settings.pegAir
                          : questionSchema.get('version') > 0 && 'regular' == questionSchema.get('shelf_type')
                            ? 0
                            : settings.air) *
                          (backupPack ? 0 : 1)) *
                      widthFactor,
                    width:
                      (widthIsSplit && !settings.fridge ? width / sectionsCount + (settings.holders ? 10 : 0) : width) -
                      settings.walls +
                      1
                  }}
                >
                  <div
                    className={shelfStyle.shelfBefore}
                    style={{
                      backgroundImage: settings.holders
                        ? 'url(' + require('./svg/' + settings.styleClass + 'Holders.svg') + ')'
                        : 'none'
                    }}
                  />
                  {('fridgeTypeTarget' == settings.styleClass || 'counterFridge' == settings.styleClass) && (
                    <div
                      className={shelfStyle.dotsShelf}
                      style={{
                        backgroundImage:
                          'fridgeTypeTarget' == settings.styleClass
                            ? 'url(' + require('./svg/dotsFridge.svg') + ')'
                            : 'auto'
                      }}
                    />
                  )}
                  <div
                    className={shelfStyle.gradientShelf}
                    style={{
                      backgroundImage:
                        'fridgeTypeTarget' == settings.styleClass
                          ? 'url(' +
                            require(
                              questionSchema.get('double_width')
                                ? first
                                  ? './svg/fridge-shelf-left.svg'
                                  : './svg/fridge-shelf-right.svg'
                                : './svg/fridge-shelf.svg'
                            ) +
                            ')'
                          : 'auto'
                    }}
                  />
                  <div
                    className={`${shelfStyle.bottomShelf} ${shelfStyle.topItem}`}
                    style={{
                      backgroundImage:
                        settings.holders && settings.double && i == shelvesMask.size - 1
                          ? 'url(' + bottomImage + ')'
                          : 'auto'
                    }}
                  >
                    <div className={shelfStyle.before} />
                    <div className={shelfStyle.after} />
                  </div>
                  <div
                    className={shelfStyle.shelfAfter}
                    style={{
                      backgroundImage: settings.holders
                        ? 'url(' + require('./svg/' + settings.styleClass + 'Holders.svg') + ')'
                        : 'none'
                    }}
                  />
                </div>
              </Fragment>
            ))}
          </div>
          {settings.double && (
            <div
              className={`${shelfStyle.pixiDiv} ${shelfStyle[settings.styleClass]} ${
                'flex' == questionSchema.get('shelf_align') ? shelfStyle.flexed : ''
              } ${(hovered || externalHover) && questionSchema.get('blur') ? shelfStyle.blurred : ''}`}
              id="pixiDiv"
              data-testid="SW-SH-2DXContainer"
              ref={pixiDiv}
            />
          )}
        </div>
        {!settings.double && (
          <div
            className={`${shelfStyle.pixiDiv} ${generateImage ? shelfStyle.generatedImage : ''} ${
              shelfStyle[settings.styleClass]
            }  ${
              'flex' == questionSchema.get('shelf_align')
                ? shelfStyle.flexed
                : (hovered || externalHover) && questionSchema.get('blur')
                  ? shelfStyle.blurred
                  : ''
            }`}
            id="pixiDiv"
            data-testid="SW-SH-2DXContainer"
            ref={pixiDiv}
            style={{ width: width }}
          />
        )}

        {hovered && hovered.src && (
          <div
            className={
              `${shelfStyle.hoveredImage} ${shelfStyle[settings.styleClass]} ` +
              (!first && settings.double ? `${shelfStyle.second} ` : ``)
            }
            data-testid="SW-SH-HoveredProduct"
            style={{
              backgroundImage: `url(${hovered.src})`,
              width: hovered.width + 1,
              height: hovered.height,
              top: hovered.y,
              left: Math.min(hovered.x, width - hovered.width * 1.25),
              backgroundPositionY: -hovered.marginTop || 0
            }}
          />
        )}
        {!questionSchema.get('hide_price') && (
          <div
            className={`${(hovered || externalHover) && questionSchema.get('blur') ? shelfStyle.blurredPrices : ''}`}
          >
            <Prices
              questionSchema={questionSchema}
              fullscreen={fullscreen}
              settings={settings}
              backupPack={backupPack}
              mobile={mobile}
              width={width}
              answers={answers}
              topShelf={topShelf}
              onProductClick={onProductClick}
              first={first}
              palletSectionIndex={palletSectionIndex}
              palletSectionsCount={palletSectionsCount}
              shelvesMask={shelvesMask}
              coordinates={coordinates}
              counts={counts}
              config={config}
            />
          </div>
        )}
      </div>
    </Fragment>
    // </Fragment>
  )
}

export default memo(
  Shelves,
  (prevProps, nextProps) =>
    prevProps.questionSchema === nextProps.questionSchema &&
    prevProps.fullscreen === nextProps.fullscreen &&
    prevProps.scrolling === nextProps.scrolling &&
    prevProps.mobile === nextProps.mobile &&
    prevProps.widthFactor === nextProps.widthFactor &&
    prevProps.width === nextProps.width &&
    prevProps.emulatedMobile === nextProps.emulatedMobile &&
    prevProps.answers === nextProps.answers &&
    prevProps.config === nextProps.config &&
    prevProps.takenGoods === nextProps.takenGoods &&
    prevProps.cartCount === nextProps.cartCount &&
    prevProps.first === nextProps.first &&
    prevProps.externalHover === nextProps.externalHover &&
    prevProps.answersImages === nextProps.answersImages &&
    prevProps.onProductClick === nextProps.onProductClick
)
